"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/store/authStore";
import {
  Anchor,
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Building2,
  CheckCircle2,
  Compass,
  FileWarning,
  GraduationCap,
  MessageSquare,
  Radar,
  Search,
  ShieldCheck,
  TrendingUp,
  Wrench,
} from "lucide-react";

export interface PlatformStats {
  // Platform depth
  courses: number;
  shorePositions: number;
  shorePositionsWithGuidance: number;
  skillGapRules: number;
  skills: number;
  ranks: number;
  certifications: number;
  vesselTypes: number;
  vesselCategories: number;
  countries: number;
  // Marketplace
  seaJobs: number;
  shoreJobs: number;
  employers: number;
  trainingInstitutes: number;
  courseOfferings: number;
  openBatches: number;
  availableSeats: number;
  seafarers: number;
  shoreProfessionals: number;
  // Legacy keys
  activeSeafarers: number;
  activeShoreProfessionals: number;
  openPositions: number;
  generatedAt?: string;
}

export const ZERO_STATS: PlatformStats = {
  courses: 0,
  shorePositions: 0,
  shorePositionsWithGuidance: 0,
  skillGapRules: 0,
  skills: 0,
  ranks: 0,
  certifications: 0,
  vesselTypes: 0,
  vesselCategories: 0,
  countries: 0,
  seaJobs: 0,
  shoreJobs: 0,
  employers: 0,
  trainingInstitutes: 0,
  courseOfferings: 0,
  openBatches: 0,
  availableSeats: 0,
  seafarers: 0,
  shoreProfessionals: 0,
  activeSeafarers: 0,
  activeShoreProfessionals: 0,
  openPositions: 0,
};

const personas = [
  {
    title: "Seafarers",
    description: "Build a structured profile. Discover sea-going opportunities matched to your rank and experience.",
    icon: Anchor,
  },
  {
    title: "Shore Professionals",
    description: "Present your maritime and shore experience. Find roles in crewing, technical, commercial, and compliance.",
    icon: Compass,
  },
  {
    title: "Employers",
    description: "Publish genuine vacancies. Discover suitable candidates through structured maritime profiles.",
    icon: Building2,
  },
];

const capabilities = [
  {
    title: "Career intelligence",
    description: "See exactly which shore roles your sea time and certificates qualify you for, and what's missing to get there.",
    icon: TrendingUp,
  },
  {
    title: "Direct messaging",
    description: "Talk to employers and candidates without leaving the platform. No forwarded emails, no lost threads.",
    icon: MessageSquare,
  },
  {
    title: "Verified documents",
    description: "Certificates and sea service records live in one place, with expiry tracked so nothing lapses quietly.",
    icon: ShieldCheck,
  },
];

const instituteResources = [
  {
    title: "Facilities",
    description: "List simulator time, classrooms and workshops when they're free. Find capacity in a city where you have demand but no site.",
    icon: Wrench,
  },
  {
    title: "Faculty",
    description: "Post specialist instructor requirements. Take visiting work from institutes that need your expertise for a batch.",
    icon: GraduationCap,
  },
  {
    title: "Discovery",
    description: "See what other institutes are running. Refer students to courses you don't offer, and take referrals for the ones you do.",
    icon: Search,
  },
];

type MetricItem = {
  key: keyof PlatformStats;
  label: string;
  href?: string;
};

function omitZeros(items: MetricItem[], stats: PlatformStats) {
  return items
    .map((item) => ({ ...item, value: (stats[item.key] as number) ?? 0 }))
    .filter((item) => item.value > 0);
}

function useInViewOnce(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options, inView]);

  return [ref, inView] as const;
}

function useCountUp(target: number, inView: boolean, reduced: boolean) {
  const [value, setValue] = useState(reduced ? target : 0);
  const rafRef = useRef<number | undefined>(undefined);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    if (!inView || startedRef.current) return;
    startedRef.current = true;
    let start: number | undefined;
    const duration = 1500;

    const step = (ts: number) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, inView, reduced]);

  return value;
}

function AnimatedFigure({
  value,
  label,
  size = "large",
}: {
  value: number;
  label: string;
  size?: "large" | "small";
}) {
  const [ref, inView] = useInViewOnce({ threshold: 0.2, rootMargin: "-80px" });
  const reduced = useReducedMotion();
  const display = useCountUp(value, inView, !!reduced);
  const valueClass =
    size === "large"
      ? "text-4xl sm:text-5xl lg:text-6xl"
      : "text-2xl sm:text-3xl";

  return (
    <div ref={ref} className="flex flex-col">
      <span className={`font-display font-bold text-white ${valueClass}`}>
        {display.toLocaleString("en-US")}
      </span>
      <span className="mt-2 text-sm text-ash">{label}</span>
    </div>
  );
}

function MetricPill({
  value,
  label,
  href,
}: {
  value: number;
  label: string;
  href?: string;
}) {
  const [ref, inView] = useInViewOnce({ threshold: 0.2, rootMargin: "-80px" });
  const reduced = useReducedMotion();
  const display = useCountUp(value, inView, !!reduced);
  const body = (
    <>
      <span className="font-semibold text-white">
        {display.toLocaleString("en-US")}
      </span>{" "}
      <span className="text-ash">{label}</span>
    </>
  );

  return (
    <span ref={ref} className="inline-flex items-baseline gap-1">
      {href ? (
        <Link
          href={href}
          className="inline-flex items-baseline gap-1 transition-colors hover:text-neon-cyan"
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </span>
  );
}

/**
 * Subtle rotating radar sweep, purely decorative.
 * Same reveal/reduced-motion pattern as AboutMaritimeRoute.tsx.
 */
function RadarBackdrop() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 600"
      className="pointer-events-none absolute left-1/2 top-1/2 h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 opacity-[0.16]"
    >
      <circle cx="300" cy="300" r="90" fill="none" stroke="#00f3ff" strokeWidth="1" />
      <circle cx="300" cy="300" r="180" fill="none" stroke="#00f3ff" strokeWidth="1" />
      <circle cx="300" cy="300" r="270" fill="none" stroke="#00f3ff" strokeWidth="1" />
      <g className="radar-sweep" style={{ transformOrigin: "300px 300px" }}>
        <path d="M 300 300 L 300 30 A 270 270 0 0 1 543.8 165 Z" fill="url(#radarGradient)" />
      </g>
      <defs>
        <linearGradient id="radarGradient" x1="300" y1="300" x2="543.8" y2="165" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#00f3ff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#00f3ff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <style>{`
        .radar-sweep { animation: radarRotate 8s linear infinite; }
        @keyframes radarRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .radar-sweep { animation: none; }
        }
      `}</style>
    </svg>
  );
}

export function HomeContent({ stats }: { stats?: PlatformStats | null }) {
  const s = stats ?? ZERO_STATS;
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 16 },
    visible: { opacity: 1, y: 0 },
  };

  const primaryDepth = omitZeros(
    [
      { key: "shorePositions", label: "Shore roles mapped" },
      { key: "shorePositionsWithGuidance", label: "With skill-gap guidance" },
      { key: "skillGapRules", label: "Skill-gap rules" },
      { key: "courses", label: "Courses linked" },
    ],
    s
  );
  const secondaryDepth = omitZeros(
    [
      { key: "skills", label: "Skills" },
      { key: "ranks", label: "Ranks" },
      { key: "certifications", label: "Certificates" },
      { key: "vesselTypes", label: "Vessel types" },
      { key: "vesselCategories", label: "Vessel categories" },
      { key: "countries", label: "Countries" },
    ],
    s
  );
  const liveMarket = omitZeros(
    [
      { key: "seaJobs", label: "Sea-going roles", href: "/jobs" },
      { key: "shoreJobs", label: "Shore roles", href: "/jobs" },
      { key: "seafarers", label: "Seafarers" },
      { key: "shoreProfessionals", label: "Shore professionals" },
      { key: "trainingInstitutes", label: "Training institutes" },
      { key: "courseOfferings", label: "Course offerings", href: "/courses" },
      { key: "openBatches", label: "Open batches" },
      { key: "availableSeats", label: "Seats available" },
    ],
    s
  );
  const trainingMarket = omitZeros(
    [
      { key: "trainingInstitutes", label: "Training institutes" },
      { key: "courseOfferings", label: "Course offerings", href: "/courses" },
      { key: "openBatches", label: "Open batches" },
      { key: "availableSeats", label: "Seats available" },
    ],
    s
  );

  const showPlatformDepth = primaryDepth.length > 0;
  const showLiveMarket = liveMarket.length > 0;
  const showTraining =
    (s.trainingInstitutes > 0 || s.courseOfferings > 0) && trainingMarket.length > 0;

  return (
    <main className="min-h-screen overflow-hidden bg-obsidian text-foreground">
      {/* Section 1 — Hero */}
      <section className="relative px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
        <RadarBackdrop />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_10%,rgba(0,243,255,0.18),transparent_30%),radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.16),transparent_32%)]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neon-cyan/20 bg-neon-cyan/8 px-4 py-2 text-sm font-semibold text-neon-cyan">
              <Radar className="h-4 w-4" /> Verified maritime hiring network
            </div>
            <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              More Than Your Next Job. Your Next Move.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ash">
              CrewAhead connects your experience, certifications and sea time to opportunities at sea and ashore — and shows you the path between them. Built by a master mariner, for the people who sail.
            </p>
            <div className="mt-9">
              <HeroCtas />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="rounded-[2rem] border border-neon-cyan/15 bg-panel/70 p-4 shadow-2xl shadow-neon-cyan/10 backdrop-blur"
          >
            <div className="rounded-[1.5rem] border border-neon-cyan/10 bg-obsidian p-5 text-white">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-neon-cyan">Match board</p>
                  <p className="mt-1 text-lg font-bold">Chief Officer · Oil/Chemical</p>
                </div>
                <ShieldCheck className="h-8 w-8 text-neon-green" />
              </div>
              <div className="mt-5 space-y-3">
                <Signal label="Certificates" value="Ready" tone="green" />
                <Signal label="Tanker matrix" value="92% match" tone="cyan" />
                <Signal label="Documents" value="2 expiring" tone="amber" />
              </div>
              <div className="mt-6 rounded-2xl border border-neon-cyan/20 bg-neon-cyan/8 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
                  <BadgeCheck className="h-4 w-4" /> Simple status for every manager
                </p>
                <p className="mt-2 text-sm text-ash">Green means ready. Yellow means check soon. Red means action needed.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 2 — Platform depth */}
      {showPlatformDepth && (
        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">What&apos;s already built</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
                Every shore role mapped. Every skill gap costed.
              </h2>
              <p className="mt-4 text-lg leading-8 text-ash">
                Career intelligence isn&apos;t a promise here — it&apos;s a catalogue that already exists. These are live counts from the platform, not projections.
              </p>
            </div>
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {primaryDepth.map((item) => (
                <AnimatedFigure
                  key={item.key}
                  value={item.value}
                  label={item.label}
                  size="large"
                />
              ))}
            </div>
            {secondaryDepth.length > 0 && (
              <div className="mt-12 grid gap-6 border-t border-white/10 pt-10 sm:grid-cols-2 lg:grid-cols-6">
                {secondaryDepth.map((item) => (
                  <AnimatedFigure
                    key={item.key}
                    value={item.value}
                    label={item.label}
                    size="small"
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Section 3 — Live on CrewAhead */}
      {showLiveMarket && (
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">Live right now</p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl">
                The network, as it stands today.
              </h2>
              <p className="mt-4 text-base leading-7 text-ash">
                Open roles, professionals and training partners currently on the platform. Small numbers, honestly reported — this is week one, not year five.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-baseline gap-x-3 gap-y-2 text-sm">
              {liveMarket.map((item, i) => (
                <span key={item.key} className="inline-flex items-center gap-3">
                  <MetricPill value={item.value} label={item.label} href={item.href} />
                  {i < liveMarket.length - 1 && <span className="text-ash">·</span>}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 4 — Three paths */}
      <section id="platform" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">One platform, three clear paths</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">Plain language for global crewing teams.</h2>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {personas.map((persona, i) => (
              <RevealCard key={persona.title} delay={i * 0.08}>
                <PersonaCard {...persona} />
              </RevealCard>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Sea to shore */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">Sea to shore</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Your sea time already qualifies you. Here&apos;s for what.
            </h2>
            <p className="mt-4 text-lg leading-8 text-ash">
              The industry keeps asking seafarers to start over. It shouldn&apos;t. Cargo planning is port operations. Maintenance discipline is technical superintendence. CrewAhead names the overlap — and the gap.
            </p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {[
              {
                from: "Chief Officer",
                to: "Port Operations Manager",
                transfers: ["Cargo planning", "Stability calculations", "Stowage", "Port interface"],
                gaps: ["Terminal systems", "Commercial contracts"],
              },
              {
                from: "Second Engineer",
                to: "Technical Superintendent",
                transfers: ["Planned maintenance", "Machinery diagnostics", "Dry-dock preparation"],
                gaps: ["Fleet budgeting", "Multi-vessel oversight"],
              },
              {
                from: "Master",
                to: "Marine Manager / DPA",
                transfers: ["ISM compliance", "Incident command", "Audit readiness"],
                gaps: ["Shore-side SMS ownership", "Regulatory reporting"],
              },
            ].map((card, i) => (
              <RevealCard key={card.from} delay={i * 0.08}>
                <div className="rounded-[1.75rem] border border-neon-cyan/10 bg-panel p-6 shadow-xl shadow-black/20">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                    <span className="font-display text-sm font-bold text-neon-cyan">{card.from}</span>
                    <span className="text-xs text-ash md:hidden">becomes</span>
                    <ArrowRight className="hidden h-4 w-4 text-ash md:block" />
                    <span className="font-display text-sm font-bold text-neon-purple">{card.to}</span>
                  </div>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-neon-green">Transfers</p>
                      <ul className="mt-2 space-y-1 text-sm text-ash">
                        {card.transfers.map((t) => (
                          <li key={t} className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neon-green" /> {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-neon-amber">Gap to close</p>
                      <ul className="mt-2 space-y-1 text-sm text-ash">
                        {card.gaps.map((g) => (
                          <li key={g} className="flex items-start gap-2">
                            <span className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border border-neon-amber/50" /> {g}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </RevealCard>
            ))}
          </div>
          <p className="mt-8 text-sm text-ash">
            Every one of the {s.shorePositions.toLocaleString("en-US")} shore roles on CrewAhead has this breakdown.{" "}
            {s.shorePositionsWithGuidance.toLocaleString("en-US")} of them carry course recommendations to close the gap.
          </p>
        </div>
      </section>

      {/* Section 6 — Beyond the job board */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">Beyond the job board</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">Built for the whole career, not just the next contract.</h2>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {capabilities.map((capability, i) => (
              <RevealCard key={capability.title} delay={i * 0.08}>
                <PersonaCard {...capability} />
              </RevealCard>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — Training, connected */}
      {showTraining && (
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-amber">Maritime training</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
                Training that matches the gap, not the brochure.
              </h2>
              <p className="mt-4 text-lg leading-8 text-ash">
                When career intelligence identifies what you&apos;re missing, it points at real courses from real institutes — with dates, locations and seats.
              </p>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {trainingMarket.map((item) => (
                <AnimatedFigure
                  key={item.key}
                  value={item.value}
                  label={item.label}
                  size="small"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 8 — Shared resources */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">Institute collaboration</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              A simulator sitting idle is a batch someone else couldn&apos;t fill.
            </h2>
            <p className="mt-4 text-lg leading-8 text-ash">
              Training institutes on CrewAhead can list spare capacity and find it. A bridge simulator free on Thursdays. A visiting LNG instructor with a week open. A classroom in a port city where you have students but no room. The equipment already exists — the coordination didn&apos;t.
            </p>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {instituteResources.map((resource, i) => (
              <RevealCard key={resource.title} delay={i * 0.08}>
                <PersonaCard {...resource} />
              </RevealCard>
            ))}
          </div>
          <div className="mt-8">
            <Link
              href="/register"
              className="inline-flex items-center gap-2 rounded-2xl border border-neon-amber/30 bg-neon-amber/8 px-6 py-3 font-bold text-neon-amber transition hover:border-neon-amber/60"
            >
              List your institute <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 9 — For employers */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">For employers</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Structured profiles. Not CV attachments.
            </h2>
            <p className="mt-4 text-lg leading-8 text-ash">
              Filter by rank, vessel type, certificate validity and sea time — because that&apos;s how crewing actually decides. Candidates are matched on maritime context, not keyword overlap, and you see readiness before you make the call.
            </p>
          </div>
          <div className="mt-8">
            <Link
              href="/register"
              className="inline-flex items-center gap-2 rounded-2xl bg-neon-purple px-6 py-3 font-bold text-white transition hover:bg-neon-purple/90"
            >
              Post a role <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 10 — Knowledge hub */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-5 rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 text-center text-white md:flex-row md:justify-between md:text-left">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
              <BookOpen className="h-4 w-4" /> From the knowledge hub
            </p>
            <p className="mt-2 text-lg font-bold">Certificate guides, sea-to-shore stories, and industry updates.</p>
          </div>
          <Link href="/blog" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan px-6 py-3 font-bold text-obsidian transition hover:bg-neon-cyan/90">
            Read the hub <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Section 11 — Feedback */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-5 rounded-[2rem] border border-neon-purple/15 bg-panel p-8 text-center text-white md:flex-row md:justify-between md:text-left">
          <div>
            <p className="text-sm font-semibold text-neon-purple">Help us build a better CrewAhead.</p>
            <p className="mt-2 text-lg font-bold">Your experience can shape what we build next.</p>
          </div>
          <Link href="/feedback" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-purple px-6 py-3 font-bold text-white transition hover:bg-neon-purple/90">
            Share Feedback <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Section 12 — Closing CTA */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 text-white md:flex-row md:items-center md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-neon-amber">
              <FileWarning className="h-4 w-4" /> Built for high-stakes maritime work
            </p>
            <h2 className="mt-2 text-2xl font-bold">Your maritime career. Better organised. Better connected.</h2>
          </div>
          <Link href="/register" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan px-6 py-3 font-bold text-obsidian transition hover:bg-neon-cyan/90">
            Get Started <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}

function HeroCtas() {
  const { isAuthenticated } = useAuth();
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const destination = isAuthenticated ? "/dashboard" : "/register";

  if (!isHydrated) {
    return (
      <div
        className="flex flex-col gap-3 sm:flex-row"
        aria-hidden="true"
        data-testid="hero-ctas-skeleton"
      >
        <div className="h-14 w-full animate-pulse rounded-2xl bg-white/10 sm:w-44" />
        <div className="h-14 w-full animate-pulse rounded-2xl bg-white/10 sm:w-52" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row" data-testid="hero-ctas">
      <Link
        href={destination}
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan px-7 py-4 font-bold text-obsidian shadow-glow-cyan transition hover:scale-[1.01]"
      >
        <Anchor className="h-5 w-5" /> Find your next move
      </Link>
      <Link
        href="/register"
        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-neon-purple/30 bg-neon-purple/8 px-7 py-4 font-bold text-neon-purple transition hover:border-neon-purple/60"
      >
        <Building2 className="h-5 w-5" /> For employers
      </Link>
    </div>
  );
}

function RevealCard({ children, delay }: { children: React.ReactNode; delay: number }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.4, delay }}
    >
      {children}
    </motion.div>
  );
}

function Signal({ label, value, tone }: { label: string; value: string; tone: "green" | "cyan" | "amber" }) {
  const toneClass = tone === "green" ? "text-neon-green" : tone === "amber" ? "text-neon-amber" : "text-neon-cyan";
  return (
    <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <span className="text-sm text-ash">{label}</span>
      <span className={`flex items-center gap-2 text-sm font-bold ${toneClass}`}><CheckCircle2 className="h-4 w-4" /> {value}</span>
    </div>
  );
}

function PersonaCard({ title, description, icon: Icon }: { title: string; description: string; icon: LucideIcon }) {
  return (
    <article className="rounded-[1.75rem] border border-neon-cyan/10 bg-panel p-6 shadow-xl shadow-black/20 transition hover:-translate-y-1 hover:border-neon-cyan/30">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-neon-cyan/10 text-neon-cyan">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-5 text-xl font-bold">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-ash">{description}</p>
    </article>
  );
}
